import useSWR from 'swr';
import { useMemo } from 'react';
import { getAuthenticatedHeaders } from './fetchHeaders';
import { logFailedRequestTiming, logRequestTiming } from './insightsHelper';
import { groupArrayByPropertyName } from '../helpers/arrayHelpers';

export const useDownloadMetadata = (partNumber) => {
    const { metadata, isLoading: isMetadataLoading } = useDocumentMetadata(partNumber);
    const { outline, pageCount, sections, hasCover } = metadata;

    let filteredChildren = [];
    if (!!outline && !!outline.children && outline.children.length > 0) {
        filteredChildren = outline.children.filter(it => !!it.pageNumber && !!it.title);
    }

    if (filteredChildren.length === 1 && !!filteredChildren[0].children &&
        filteredChildren[0].children.length > 0) {
        filteredChildren = filteredChildren[0].children.filter(it => !!it.pageNumber && !!it.title);
    }

    const sectionGroups = useMemo(() => {
        if (!!sections && !!filteredChildren && filteredChildren.length > 0) {
            return groupArrayByPropertyName(sections.map(s => {
                const parent = filteredChildren.filter(item => s.start >= (hasCover ? item.pageNumber - 1 : item.pageNumber)).reverse()[0];
                if (!parent) {
                    return {
                        invalid: true
                    };
                }
                const children = parent.children

                let invalid = parent.isFrontMatter;
                let title = !!parent.sectionNumber ? `${parent.sectionNumber}` : '';
                const sectionTitle = parent.title;
                if (!!children && children.length > 0) {
                    const item = children.filter(c => s.start === (hasCover ? c.pageNumber - 1 : c.pageNumber) && !!c.title)[0];

                    if (!item) {
                        invalid = true;
                    }

                    if (!invalid && !!item.sectionNumber && item.title.length < 5) {
                        invalid = true;
                    }

                    title += item?.title;
                } else {
                    invalid = true;
                }

                return {
                    invalid: invalid,
                    start: s.start,
                    end: s.end,
                    title: title,
                    fileName: s.fileName,
                    sectionTitle: sectionTitle
                };
            }).filter(it => !it.invalid), 'sectionTitle');
        }

        return {};
    }, [sections && filteredChildren]);
    const sectionGroupKeys = Object.keys(sectionGroups);

    const isLoaded = !!metadata && !isMetadataLoading;
    const hasMultipleSections = filteredChildren?.length > 2 && pageCount > 25 && sectionGroupKeys.length > 1;

    return { isLoaded, hasMultipleSections, sectionGroupKeys, sectionGroups, hasCover };
}

export const useDocumentMetadata = (partNumber) => {
    const startTime = performance.now(); // Start timing
    const { data, error, isValidating, isLoading, mutate } = useSWR(
        getPath(partNumber),
        fetcher,
        {
            ...swrConfig,
            onSuccess: (_data, _key) => {
                const endTime = performance.now(); // End timing
                logRequestTiming('/document/${partNumber}/metadata', Math.round(endTime - startTime));
            },
            onError: (_error, key) => {
                const endTime = performance.now(); // End timing
                logFailedRequestTiming(key, Math.round(endTime - startTime));
            }
        });

    let localError = error;
    if (data?.status >= 400 && !error) {
        localError = data;
    }

    const metadata = data || { pageCount: 0 };

    return { metadata, error: localError, isValidating, isLoading, mutate };
};

const getPath = (partNumber) => {
    if (!partNumber) {
        return null;
    }

    return `/document/${partNumber}/metadata`;
}

export const useVideoMetadata = (partNumber) => {
    const startTime = performance.now(); // Start timing
    const { data, error, isValidating, isLoading, mutate } = useSWR(
        !!partNumber ? `/document/${partNumber}/videoMetadata` : null,
        fetcher,
        {
            ...swrConfig,
            onSuccess: (_data, _key) => {
                const endTime = performance.now(); // End timing
                logRequestTiming('/document/${partNumber}/videoMetadata', Math.round(endTime - startTime));
            },
            onError: (_error, key) => {
                const endTime = performance.now(); // End timing
                logFailedRequestTiming(key, Math.round(endTime - startTime));
            }
        });

    let localError = error;
    if (data?.status >= 400 && !error) {
        localError = data;
    }

    const metadata = data || { pageCount: 0 };

    return { metadata, error: localError, isValidating, isLoading, mutate };
};

export const useRelatedDocuments = (partNumber) => {
    const startTime = performance.now(); // Start timing
    const { data, error, isValidating, isLoading, mutate } = useSWR(
        getRelatedPath(partNumber),
        fetcher,
        {
            ...swrConfig,
            onSuccess: (_data, _key) => {
                const endTime = performance.now(); // End timing
                logRequestTiming('/document/${partNumber}/metadata/related', Math.round(endTime - startTime));
            },
            onError: (_error, key) => {
                const endTime = performance.now(); // End timing
                logFailedRequestTiming(key, Math.round(endTime - startTime));
            }
        });

    const related = data || [];

    return { related, error, isValidating, isLoading, mutate };
};

const getRelatedPath = (partNumber) => {
    if (!partNumber) {
        return null;
    }

    return `/document/${partNumber}/metadata/related`;
}

export const useDocumentMetadataSearch = (partNumber, query) => {
    const startTime = performance.now(); // Start timing
    const { data, error, isValidating, isLoading, mutate } = useSWR(
        getSearchPath(partNumber, query),
        fetcher,
        {
            ...swrConfig,
            onSuccess: (_data, key) => {
                const endTime = performance.now(); // End timing
                logRequestTiming(key, Math.round(endTime - startTime));
            },
            onError: (_error, key) => {
                const endTime = performance.now(); // End timing
                logFailedRequestTiming(key, Math.round(endTime - startTime));
            }
        });

    const items = data || [];

    return { items, error, isValidating, isLoading, mutate };
};

const getSearchPath = (partNumber, query) => {
    if (!partNumber || !query) {
        return null;
    }

    return `/document/${partNumber}/search?query=${query}`;
}

const fetcher = (url) => {
    return getAuthenticatedHeaders().then((headers) => {
        return fetch(url, { headers }).then(response => response.json())
    });
}

const swrConfig = {
    revalidateOnFocus: false,
    onErrorRetry: (error, key, config, revalidate, { retryCount }) => {
        // Never retry on 4XX error.
        if (error.status >= 400 && error.status < 500) return;

        //   // Never retry for a specific key.
        //   if (key === '/api/user') return

        // Only retry up to 1 times.
        if (retryCount > 1) return;

        // Retry after 1 seconds.
        setTimeout(() => { revalidate({ retryCount }); }, 1000)
    }
};